import { Color, ColorResult, RGBColor, SketchPicker } from 'react-color';
import * as Page from '../../../components/content/page-header';
import { Content } from '../../../components/layout/content';
import * as Button from '../../../components/button/button';
import { Icons } from '../../../components/icons/icons';
import { PaginatedTable } from '../../../components/table/paginated-table';
import usePageState from '../../../hooks/use-page-state';
import { Modes } from '../../../types/page-modes';
import { Actions, Dialog, Title, Content as DialogContent } from '../../../components/dialog/dialog';
import { Label, TextInputLarge } from '../../../components/forms/input';
import { FieldValues, useForm } from 'react-hook-form';
import React, { MouseEvent, useState } from 'react';
import { Pageable, Query, usePagination } from '../../../hooks/use-pagination';
import { useDeleteMutation, usePostMutation, usePutMutation } from '../../../hooks/use-mutations';
import { HazardSeverity } from './types';
import { ActionColumn } from '../../../components/table/columns';

export const HazardSeverities = (): JSX.Element => {
  const [pageState, setPageState] = usePageState();
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm();
  const [colorPickerVisibility, setColorPickerVisibility] = useState<boolean>(false);
  const [currentColor, setCurrentColor] = useState<Color>();
  const color = watch('color');
  const [query, setQuery] = useState<Query>({});
  const hazardSeverities = usePagination<Pageable<HazardSeverity>>('/hazard-severities', query);
  const postMutation = usePostMutation({ path: '/hazard-severities' });
  const putMutation = usePutMutation({ path: `/hazard-severities/${pageState.item?.id as string}` });
  const deleteMutation = useDeleteMutation({ cacheKey: '/hazard-severities' });

  const onSubmit = (data: FieldValues): void => {
    !data.id ? postMutation.mutate(data) : putMutation.mutate(data);
    setPageState(Modes.BROWSE);
  };

  const addHazardSeverity = (): void => {
    reset();
    setColorPickerVisibility(false);
    setPageState(Modes.CREATE);
  };

  const onDeleteConfirm = (): void => {
    deleteMutation.mutate({ path: `/hazard-severities/${pageState.item?.id as string}` });
    setPageState(Modes.BROWSE);
  };

  const onUpdate = (item: HazardSeverity): void => {
    setColorPickerVisibility(false);
    for (const key in item) {
      setValue(key, item[key as keyof HazardSeverity]);
    }
    setPageState(Modes.UPDATE, item);
  };

  const onChangeComplete = (color: ColorResult): void => {
    setCurrentColor(color.rgb);
    setValue('color', JSON.stringify(color.rgb));
  };

  const onChange = (color: ColorResult): void => {
    setCurrentColor(color.rgb);
  };

  const showColorPicker = (event: MouseEvent<HTMLDivElement>): void => {
    event.stopPropagation();
    setColorPickerVisibility(true);
  };

  const getStyleFromColor = (): { background?: string } => {
    try {
      const parsedColor = JSON.parse(color as string) as RGBColor;
      return {
        background: `rgba(${String(parsedColor.r)},${String(parsedColor.g)},${String(parsedColor.b)},${String(parsedColor.a)})`
      };
    } catch(exception) {
      console.error(exception);
    }
    return {};
  };

  const columns = [
    { accessor: 'key', Header: 'Id', sort: true },
    { accessor: 'name', Header: 'Name' },
    { accessor: 'color', Header: 'Color', Cell: (item: any): JSX.Element => {
      return renderColorElement(item.row.original.color as string);
    } },
    ActionColumn({ onDelete: (item: HazardSeverity): void => setPageState(Modes.DELETE, item), onUpdate })
  ];

  return <>
    <Content>
      <Page.Header>
        <Page.Title>Hazard Severities</Page.Title>
        <Page.SubTitle>
          Hazard Severities in DisasterAWARE.
          <div>
            <Button.LightBlackSmall onClick={addHazardSeverity}>
              <Icons.Add />
              Add Hazard Severity
            </Button.LightBlackSmall>
          </div>
        </Page.SubTitle>
      </Page.Header>
      <PaginatedTable isLoading={hazardSeverities.isLoading} columns={columns} data={hazardSeverities.data} onChange={setQuery} />
    </Content>
    {(pageState.mode === Modes.CREATE || pageState.mode === Modes.UPDATE) && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <form onSubmit={handleSubmit(onSubmit)} onClick={(): void => setColorPickerVisibility(false)}>
          <Title>{pageState.mode === Modes.CREATE ? 'Add Hazard Severity' : 'Edit Hazard Severity'}</Title>
          <DialogContent>
            <div className="mb-4">
              <Label name="key">Hazard Severity Id</Label>
              <TextInputLarge {...register('key', { required: 'You must provide a value for Hazard Severity Id.' })} errors={errors} placeholder="Hazard Severity Id" />
            </div>
            <div className="mb-4">
              <Label name="name">Hazard Severity Name</Label>
              <TextInputLarge {...register('name', { required: 'You must provide a value for Hazard Severity Name.' })} errors={errors} placeholder="Hazard Severity Name" />
            </div>
            <div className="mb-4">
              <Label>Priority</Label>
              <TextInputLarge {...register('priority')} placeholder="Priority" />
            </div>
            <div className="mb-4 d-flex align-items-center">
              <Label className="m-0">Color</Label>
              <div className="color-field-container pointer" onClick={showColorPicker}>
                {color ? (
                  <div className="color-field" style={getStyleFromColor()} />
                ) : (
                  <div className="no-color-field">
                    <Icons.NoColor />
                  </div>
               )}
              <Icons.ArrowDown className="select-arrow" width="18" />
              {colorPickerVisibility && <SketchPicker color={currentColor} onChangeComplete={onChangeComplete} onChange={onChange} />}
              </div>
            </div>
          </DialogContent>
          <Actions>
            <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
            <Button.Primary type="submit">
              {pageState.mode === Modes.CREATE ? <><Icons.Add /> Add</> : 'Update'}
            </Button.Primary>
          </Actions>
        </form>
      </Dialog>
    )}
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Hazard Severity</Title>
        <DialogContent>Are you sure you want to delete?</DialogContent>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
  </>;
};

const renderColorElement = (json: string): JSX.Element => {
  const str = (color: any): string => String(color);
  try {
    const rgb = JSON.parse(json) as RGBColor;
    if (!rgb?.a) {
      return <span />;
    }
    const color = `rgb${rgb?.a === 1 ? '' : 'a'}(${str(rgb.r)}, ${str(rgb.g)}, ${str(rgb.b)}${rgb.a === 1 ? '' : `, ${str(rgb.a)}`})`;
    return <div className="d-flex align-items-center">
      <span className="table-color" style={{ background: color }} />{color.replace(/rgba?/, rgb.a === 1 ? 'rgb ' : 'rgba ')}
    </div>;
  } catch(exception) {
    console.error(exception);
  }
  return <span />;
};
